import { FCElement } from '@fc/component';
import { supportsViewTransitions, startViewTransition, debounce, preloadImage } from '@fc/utilities';
import { ZoomPickEvent } from '@fc/events';
import { DialogCloseEvent } from '@fc/dialog';
import { trapFocus, removeTrapFocus } from '@fc/focus';

// View transition helper: sets name on source, runs callback, moves name to dest, cleans up after
async function withViewTransition(src, dest, vtName, callback) {
  if (!supportsViewTransitions?.() || !src) {
    callback();
    return;
  }
  src.style.setProperty('view-transition-name', vtName);
  await startViewTransition(() => {
    callback();
    src.style.removeProperty('view-transition-name');
    dest?.style.setProperty('view-transition-name', vtName);
  });
  dest?.style.removeProperty('view-transition-name');
}

export class ProductZoomOverlay extends FCElement {
  requiredRefs = ['dialog', 'media'];
  #loadedHighResUrls = new Set();
  #restoreFocusTo = null;

  connectedCallback() {
    super.connectedCallback();
    const dlg = this.refs.dialog;
    if (dlg instanceof HTMLDialogElement) {
      dlg.addEventListener('scroll', this.#onDialogScroll);
      dlg.addEventListener('cancel', this.#onDialogCancel);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const dlg = this.refs.dialog;
    if (dlg instanceof HTMLDialogElement) {
      dlg.removeEventListener('scroll', this.#onDialogScroll);
      dlg.removeEventListener('cancel', this.#onDialogCancel);
    }
    removeTrapFocus(dlg);
    this.#restoreFocusTo = null;
  }

  async open(mediaIndex, event) {
    if (!Number.isFinite(mediaIndex) || mediaIndex < 0) return;
    event?.preventDefault?.();

    const { dialog: dlg, media: items } = this.refs;
    if (!(dlg instanceof HTMLDialogElement)) return;
    if (!Array.isArray(items) || !items[mediaIndex]) return;

    const dest = items[mediaIndex];
    if (!(dest instanceof HTMLElement)) return;

    this.#captureRestoreFocus(event);

    const reveal = () => {
      dlg.showModal();
      dest.scrollIntoView({ behavior: 'instant' });
      this.#bindDialogFocus();
    };

    // Find origin element for view transition
    const origin = event?.target instanceof Element ? event.target.closest('li, carousel-slide') : null;

    if (!supportsViewTransitions?.() || !(origin instanceof HTMLElement)) {
      reveal();
      this.activateThumbnail(mediaIndex, { behavior: 'instant' });
      return;
    }

    await withViewTransition(origin, dest, 'gallery-item', reveal);
    this.activateThumbnail(mediaIndex, { behavior: 'instant' });
  }

  #upgradeToHighRes(container) {
    if (!(container instanceof HTMLElement)) return false;
    if (!container.classList.contains('product-media-container--image')) return false;

    const img = container.querySelector('img.product-media__image');
    if (!(img instanceof HTMLImageElement)) return false;

    const url = img.getAttribute('data_max_resolution');
    if (!url?.trim() || this.#loadedHighResUrls.has(url)) return false;

    preloadImage?.(url);

    const hi = new Image();
    hi.className = img.className || '';
    hi.alt = img.alt || '';
    hi.setAttribute('data_max_resolution', url);

    hi.onload = () => {
      if (img.parentNode) {
        img.replaceWith(hi);
        this.#loadedHighResUrls.add(url);
      }
    };
    hi.onerror = () => {};
    hi.src = url;
    return true;
  }

  #onDialogScroll = debounce(async () => {
    const { media: items } = this.refs;
    if (!Array.isArray(items) || items.length === 0) return;

    const dominant = await findDominantVisibleElement(items);
    if (!(dominant instanceof HTMLElement)) return;

    const idx = items.indexOf(dominant);
    if (idx < 0) return;

    this.#upgradeToHighRes(dominant);
    this.dispatchEvent(new ZoomPickEvent(idx));
  }, 50);

  async close() {
    const { dialog: dlg, media: items } = this.refs;
    if (!(dlg instanceof HTMLDialogElement)) return;
    if (!Array.isArray(items) || items.length === 0) return this.dismissOverlay();
    if (!supportsViewTransitions?.()) return this.dismissOverlay();

    const dominant = await findDominantVisibleElement(items);
    if (!(dominant instanceof HTMLElement)) return this.dismissOverlay();

    const idx = items.indexOf(dominant);
    if (idx < 0) return this.dismissOverlay();

    // Locate parent gallery and resolve the target slide
    const gallery = this.closest('gallery-viewer');
    const isCarousel = gallery?.displayMode === 'carousel';
    const slide = isCarousel ? gallery?.carouselComponent?.slides?.[idx] : gallery?.mediaElements?.[idx];

    if (!(slide instanceof HTMLElement)) return this.dismissOverlay();

    dlg.classList.add('dialog--closed');

    await withViewTransition(dominant, slide, 'gallery-item', () => {
      this.dismissOverlay();
    });

    dlg.classList.remove('dialog--closed');
  }

  dismissOverlay() {
    const dlg = this.refs.dialog;
    if (!(dlg instanceof HTMLDialogElement)) return;
    removeTrapFocus(dlg);
    dlg.close();
    window.dispatchEvent(new DialogCloseEvent());
    this.#restoreFocus();
  }

  handleKeyDown(ev) {
    if (ev?.key !== 'Escape') return;
    ev.preventDefault();
    this.close();
  }

  #onDialogCancel = (event) => {
    event.preventDefault();
    void this.close();
  };

  #captureRestoreFocus(event) {
    const explicitTarget = event?.target instanceof HTMLElement ? event.target : null;
    const activeElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const candidate = explicitTarget || activeElement;
    this.#restoreFocusTo = candidate?.closest?.('button, a[href], input, select, textarea, [tabindex]:not([tabindex="-1"])') || candidate;
  }

  #bindDialogFocus() {
    const dlg = this.refs.dialog;
    if (!(dlg instanceof HTMLDialogElement) || !dlg.open) return;

    const initialFocus = dlg.querySelector('.zoom-overlay-close-btn');
    trapFocus(dlg, {
      initialFocus: initialFocus instanceof HTMLElement ? initialFocus : undefined,
    });
  }

  #restoreFocus() {
    const target = this.#restoreFocusTo;
    this.#restoreFocusTo = null;

    if (target instanceof HTMLElement && target.isConnected) {
      requestAnimationFrame(() => target.focus());
    }
  }

  async activateThumbnail(mediaIndex, opts = { behavior: 'smooth' }) {
    if (!Number.isFinite(mediaIndex) || mediaIndex < 0) return;

    const beh = opts?.behavior ?? 'smooth';
    const { media: items } = this.refs;
    const target = items?.[mediaIndex];

    if (target instanceof HTMLElement) {
      target.scrollIntoView({ behavior: beh });
      this.#upgradeToHighRes(target);
    }

    this.dispatchEvent(new ZoomPickEvent(mediaIndex));
  }
}

// Register custom element
if (!customElements.get('zoom-overlay')) {
  customElements.define('zoom-overlay', ProductZoomOverlay);
}

// Finds the element with the highest visibility ratio via IntersectionObserver
function findDominantVisibleElement(els) {
  return new Promise((resolve) => {
    const valid = els.filter((el) => el instanceof HTMLElement);
    if (valid.length === 0) {
      resolve(null);
      return;
    }
    if (valid.length === 1) {
      resolve(valid[0]);
      return;
    }

    const thresholds = Array.from({ length: 100 }, (_, i) => i / 100);
    const obs = new IntersectionObserver(
      (entries) => {
        obs.disconnect();
        if (!entries?.length) {
          resolve(valid[0]);
          return;
        }

        const best = entries.reduce((lead, cur) =>
          (cur.intersectionRatio ?? 0) > (lead.intersectionRatio ?? 0) ? cur : lead,
        );
        resolve(best.target instanceof HTMLElement ? best.target : valid[0]);
      },
      { threshold: thresholds },
    );

    for (const el of valid) obs.observe(el);
  });
}
